// End-user runtime view for a built app. Loads project files for an appId and
// renders them via AppRuntime. Used when a builder app is opened from the
// side menu — the editor (BuilderEditor) is a separate flow on the apps page.
//
// `isPublic` switches the file-loading path to the cross-company public-app
// endpoints (`/apps/public/<id>/files`, `/apps/public/<id>/file`) so end users
// in whitelabel-provisioned companies can run a Public app whose owner lives
// in a different company.

import React from "react";
import axios from "axios";
import { Alert, Box, CircularProgress } from "@mui/material";

import { hostname } from "../../utils";
import { createBuilderApi } from "./api";
import AppRuntime from "./runtime/AppRuntime";
import type { BuilderFile, BuilderFileEntry } from "./types";

interface Props {
  appId: string;
  companyId: string;
  getToken: () => Promise<string>;
  isPublic?: boolean;
}

const BuilderAppRuntime: React.FC<Props> = ({
  appId,
  companyId,
  getToken,
  isPublic,
}) => {
  const tenantApi = React.useMemo(
    () => createBuilderApi(getToken, companyId),
    [getToken, companyId],
  );
  const [files, setFiles] = React.useState<Record<string, BuilderFile> | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        setFiles(null);
        let entries: BuilderFileEntry[];
        let read: (path: string) => Promise<BuilderFile>;
        if (isPublic) {
          const headers = { Authorization: `Bearer ${await getToken()}` };
          const r = await axios.get(
            `${hostname}/apps/public/${appId}/files`,
            { headers },
          );
          entries = r.data.files || [];
          read = async (path: string) => {
            const h = { Authorization: `Bearer ${await getToken()}` };
            const f = await axios.get(
              `${hostname}/apps/public/${appId}/file`,
              { headers: h, params: { path } },
            );
            return { content: f.data.content, type: f.data.type };
          };
        } else {
          entries = await tenantApi.listFiles(appId);
          read = async (path: string) => {
            const f = await tenantApi.readFile(appId, path);
            return { content: f.content, type: f.type };
          };
        }
        const next: Record<string, BuilderFile> = {};
        await Promise.all(
          entries.map(async (e) => {
            next[e.path] = await read(e.path);
          }),
        );
        if (!cancelled) setFiles(next);
      } catch (e: any) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 404) {
          setError(
            "App not found. It may have been removed, or you no longer have access.",
          );
        } else {
          setError(String(e?.response?.data?.error || e?.message || e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantApi, appId, isPublic, getToken]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  if (!files) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
      }}
    >
      <AppRuntime
        files={files}
        getToken={getToken}
        companyId={companyId}
        appId={appId}
        hostname={hostname}
      />
    </Box>
  );
};

export default BuilderAppRuntime;
